import { useTranslation } from "react-i18next";
import { useCallback, useMemo } from "react";
import {
  DEFAULT_LANGUAGE,
  SUPPORTED_LANGUAGES,
  SupportedLanguage,
  LanguageInfo,
} from "@/i18n/types";
import { useFirebaseAuth } from "@/state/FirebaseAuthContext";
import { updateUserLanguage } from "@/helpers/firestore_helpers";
import { useFaceStore } from "@/state/FaceStore";

export function useLanguage() {
  const { i18n, t } = useTranslation();
  const { user } = useFirebaseAuth();

  const currentLanguage = useMemo((): SupportedLanguage => {
    const current = i18n.language?.split("-")[0]?.toLowerCase();
    if (current && SUPPORTED_LANGUAGES.some((lang) => lang.code === current)) {
      return current as SupportedLanguage;
    }
    return DEFAULT_LANGUAGE;
  }, [i18n.language]);

  const currentLanguageInfo = useMemo((): LanguageInfo => {
    return (
      SUPPORTED_LANGUAGES.find((lang) => lang.code === currentLanguage) ||
      SUPPORTED_LANGUAGES[0]
    );
  }, [currentLanguage]);

  const isRTL = currentLanguageInfo.dir === "rtl";
  const dir = currentLanguageInfo.dir;

  const changeLanguage = useCallback(
    async (lang: SupportedLanguage) => {
      await i18n.changeLanguage(lang);

      const langInfo = SUPPORTED_LANGUAGES.find((l) => l.code === lang);
      if (langInfo) {
        try {
          useFaceStore.getState().setLanguage(langInfo.englishName);
        } catch {
          // ignore if FaceStore is not available
        }
      }

      if (user && !user.isAnonymous) {
        try {
          await updateUserLanguage(user.uid, lang);
        } catch (e) {
          console.warn("Failed to sync language to user profile:", e);
        }
      }
    },
    [i18n, user],
  );

  return {
    currentLanguage,
    currentLanguageInfo,
    isRTL,
    dir,
    languages: SUPPORTED_LANGUAGES,
    changeLanguage,
    t,
  };
}
